import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

/**
 * The moving strips the marketing pages share: the home page's logo and
 * integration marquees and the landing pages' developer band, with the edge
 * masks that fade them out and the inline marks drawn beside them.
 */

/**
 * The gradient that fades a marquee out at its edges. Four of these on the
 * page, at three different angles, so the angle and the ground are the props.
 */
export function EdgeMask({
  angle,
  ground,
  sx,
}: {
  angle: string;
  ground: string;
  sx: SxProps<Theme>;
}) {
  return (
    <Box
      aria-hidden
      sx={[
        {
          position: "absolute",
          zIndex: 2,
          pointerEvents: "none",
          background: `linear-gradient(${angle}, ${ground}, rgba(255, 255, 255, 0))`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}

/**
 * An endless horizontal strip. The published site drives its three marquees
 * from two different scripts; a CSS keyframe over a duplicated panel is the
 * same picture with nothing to load, and it stops for a reader who has asked
 * for less motion.
 *
 * The gap rides on the panel as a trailing pad rather than on the track, which
 * is what makes the -50% exact: with a track gap the two panels and the space
 * between them are three unequal parts and the loop jumps every cycle.
 */
export function Marquee({
  duration,
  reverse,
  gap,
  children,
}: {
  duration: number;
  reverse?: boolean;
  gap: string;
  children: ReactNode;
}) {
  const panel = (duplicate?: boolean) => (
    <Box
      aria-hidden={duplicate}
      sx={{
        display: "flex",
        alignItems: "center",
        flex: "0 0 auto",
        gap,
        pr: gap,
      }}
    >
      {children}
    </Box>
  );
  return (
    <Box sx={{ display: "flex", overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          willChange: "transform",
          animation: `home-marquee ${duration}s linear infinite`,
          animationDirection: reverse ? "reverse" : "normal",
          "@keyframes home-marquee": {
            from: { transform: "translateX(0)" },
            to: { transform: "translateX(-50%)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        {panel()}
        {panel(true)}
      </Box>
    </Box>
  );
}

/** `.reliable-lbl_wrap`'s glyph and `.card_icon`'s frame share this box. */
export function InlineSvg({
  viewBox,
  size,
  sx,
  children,
}: {
  viewBox: string;
  size: number | string;
  sx?: SxProps<Theme>;
  children: ReactNode;
}) {
  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      fill="none"
      aria-hidden
      sx={[
        { width: size, height: size, flex: "0 0 auto" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}
